import React, { useMemo, useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";

interface SlotDigitProps {
  showZero: boolean;
  value?: string; // final digit to stop at
  index: number; // slot index for delay
}

const SLOT_HEIGHT_PX = 2.5 * 16; // 2.5rem = 40px
const DIGIT_COUNT = 10;
const TOTAL_DIGITS = DIGIT_COUNT * 2;

const slideUpLoop = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-${SLOT_HEIGHT_PX * TOTAL_DIGITS}px);
  }
`;

const slideDownLoop = keyframes`
  0% {
    transform: translateY(-${SLOT_HEIGHT_PX * TOTAL_DIGITS}px);
  }
  100% {
    transform: translateY(0);
  }
`;

const SlotContainer = styled.div`
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  overflow: hidden;
  border-radius: 0.5rem;
  background: white;
  border: 2px solid #5a47ea;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SlotStrip = styled.div<{
  animate: boolean;
  stopY?: number;
  delay?: number;
  direction?: "up" | "down";
}>`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  ${({ animate, direction }) =>
    animate &&
    css`
      animation: ${direction === "up" ? slideUpLoop : slideDownLoop} 3s linear
        infinite;
    `}

  ${({ animate, stopY, delay }) =>
    !animate &&
    typeof stopY === "number" &&
    css`
      transform: translateY(-${stopY}px);
      transition: transform 1s ease-out;
      transition-delay: ${delay}s;
    `}
`;

const Digit = styled.div`
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.5rem;
`;

const OverlayDigit = styled(Digit)<{ visible?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transition: opacity 0.5s ease;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  z-index: ${({ visible }) => (visible ? 2 : 1)};
`;

const SlotDigit: React.FC<SlotDigitProps> = ({ showZero, value, index }) => {
  const [stopping, setStopping] = useState(false);
  const [stopY, setStopY] = useState(0);
  const [direction, setDirection] = useState<"up" | "down">("up");

  const digits = useMemo(() => {
    const base = [...Array(10).keys()];
    const shuffled = base.sort(() => Math.random() - 0.5);
    return [...shuffled, ...shuffled]; // 20 total
  }, []);

  useEffect(() => {
    if (value && !showZero) {
      const delay = index * 0.15;
      const timeout = setTimeout(() => {
        const targetDigit = parseInt(value, 10);
        const centerIndex = DIGIT_COUNT;
        const closest = findClosestIndex(digits, targetDigit, centerIndex);

        const translateY = closest.index * SLOT_HEIGHT_PX;
        setStopY(translateY);
        setDirection(closest.direction);
        setStopping(true);
      }, delay * 1000);

      return () => clearTimeout(timeout);
    }
  }, [value, showZero]);

  return (
    <SlotContainer>
      {!value && showZero && <OverlayDigit visible>0</OverlayDigit>}

      <SlotStrip
        animate={!stopping && !showZero}
        stopY={stopY}
        delay={index * 0.15}
        direction={direction}
      >
        {digits.map((digit, i) => (
          <Digit key={i}>{digit}</Digit>
        ))}
      </SlotStrip>
    </SlotContainer>
  );
};

export default SlotDigit;

// Determine shortest direction to target digit
function findClosestIndex(
  digits: number[],
  target: number,
  centerIndex: number
): { index: number; direction: "up" | "down" } {
  const positions = digits
    .map((digit, idx) => ({ digit, idx }))
    .filter((d) => d.digit === target);

  let best = positions[0];
  let minDistance = Infinity;
  let direction: "up" | "down" = "up";

  for (const pos of positions) {
    const upDist = Math.abs(pos.idx - centerIndex);
    const downDist = Math.abs(pos.idx + TOTAL_DIGITS - centerIndex);

    if (upDist < minDistance) {
      minDistance = upDist;
      best = pos;
      direction = pos.idx >= centerIndex ? "up" : "down";
    }
  }

  return { index: best.idx, direction };
}
